import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { Download, ShieldAlert } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SkeletonRows } from '@/components/ui/skeleton'

import { useMe } from '@/features/auth/api'
import { pageCountFor, useClampPage } from '@/features/cases/paging'
import { dateTime, localDate, num, relativeTime } from '@/lib/format'

import { AuditPager, type AuditPageSize } from './AuditPager'
import {
  usePiiAccessCount,
  usePiiAccessReads,
  type PiiAccessKind,
  type PiiAccessRecord,
  type PiiAggregateType,
} from './piiAccessApi'

/**
 * DPO / compliance dashboard for the read-access audit trail (BRD §8).
 *
 * <p>Mounted at /admin/audit/pii-access and gated behind
 * PII_ACCESS_AUDIT_VIEW. Mirrors AuditPage's skeleton: a sticky filter
 * bar over a paginated table. Differences vs AuditPage: read events are
 * never aggregated by prefix (there's only VIEW / LIST / EXPORT), and
 * we surface the actor's roles inline because DPDP §11 responses
 * frequently ask "what role did they hold at the time of access".</p>
 */
export function PiiAccessPage() {
  const today = useMemo(() => isoDate(new Date()), [])
  const sevenDaysAgo = useMemo(
    () => isoDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
    [],
  )
  const [fromDate, setFromDate] = useState(sevenDaysAgo)
  const [toDate, setToDate] = useState(today)
  const [actor, setActor] = useState('')
  const [aggregateType, setAggregateType] = useState<PiiAggregateType | ''>('')
  const [aggregateId, setAggregateId] = useState('')
  const [kind, setKind] = useState<PiiAccessKind | ''>('')
  const [pageSize, setPageSize] = useState<AuditPageSize>(50)
  const [page, setPage] = useState(0)
  // Every page view adds reads to this trail. Pin the window's end to when
  // the filters were set, so the total and every page count the same reads.
  const [asOf, setAsOf] = useState(() => new Date().toISOString())

  // Backend expects ISO instants; clamp the dates to start/end of day.
  const fromIso = `${fromDate}T00:00:00Z`
  const toIso = earlier(`${toDate}T23:59:59Z`, asOf)

  const query = {
    from: fromIso,
    to: toIso,
    ...(actor ? { actor: actor.trim().toUpperCase() } : {}),
    ...(aggregateType ? { aggregateType } : {}),
    ...(aggregateId ? { aggregateId: aggregateId.trim() } : {}),
    ...(kind ? { kind } : {}),
  }

  const { data: reads, isPending, isError, error, isPlaceholderData } = usePiiAccessReads({
    ...query, limit: pageSize, offset: page * pageSize,
  })
  const { data: total } = usePiiAccessCount(query)
  const { data: me } = useMe()

  // A filter or the page size changed: back to the first page, and a fresh
  // window end. Adjusted during render, not in an effect, so the first load
  // does not fetch twice.
  const filterKey = [fromDate, toDate, actor, aggregateType, aggregateId, kind, pageSize].join('|')
  const [shownFor, setShownFor] = useState(filterKey)
  if (shownFor !== filterKey) {
    setShownFor(filterKey)
    setPage(0)
    setAsOf(new Date().toISOString())
  }
  useClampPage(page, setPage, pageCountFor(total ?? null, pageSize))

  const forbidden =
    isError && isAxiosError(error) && error.response?.status === 403
  const otherError = isError && !forbidden

  function exportCsv() {
    if (!reads) return
    const headers = [
      'accessedAt', 'accessKind', 'actorEmpId', 'actorUserId', 'actorRoles',
      'aggregateType', 'aggregateId', 'route',
      'rowsReturned', 'bytesReturned',
      'clientIp', 'userAgent', 'requestId', 'contextJson', 'readId',
    ]
    const rows = reads.map((r) => [
      r.accessedAt,
      r.accessKind,
      r.actorEmpId ?? '',
      r.actorUserId ?? '',
      r.actorRoles.join('|'),
      r.aggregateType,
      r.aggregateId ?? '',
      r.route,
      r.rowsReturned == null ? '' : r.rowsReturned.toString(),
      r.bytesReturned == null ? '' : r.bytesReturned.toString(),
      r.clientIp ?? '',
      r.userAgent ?? '',
      r.requestId ?? '',
      r.contextJson,
      r.readId,
    ])
    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const slug = me?.tenantSlug ?? 'tenant'
    a.download = `pii-access-${slug}-${today.replaceAll('-', '')}-page${page + 1}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="mx-auto max-w-[1400px] space-y-5">
      <div>
        <p className="text-2xs font-semibold uppercase tracking-wider text-navy-600">
          Tenant administration
        </p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-semibold tracking-tight text-navy-900">
          <ShieldAlert className="h-5 w-5" /> PII access trail
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every Customer 360 open, queue expand, and export. 18-month retention.
          Use this to answer &ldquo;who looked at this customer, when, why&rdquo;
          for regulator / DPDP §11 requests.
        </p>
      </div>

      {/* Filters — sticky on scroll so the header doesn't drift away */}
      <div className="sticky top-13 z-10 -mx-4 bg-navy-50/40 px-4 pt-1 pb-1 lg:-mx-6 lg:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
            <CardDescription>
              Default window is the last 7 days. Narrow by actor or subject.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
              <div>
                <Label>From</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  max={toDate}
                />
              </div>
              <div>
                <Label>To</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  max={today}
                  min={fromDate}
                />
              </div>
              <div>
                <Label>Actor (Emp ID)</Label>
                <Input
                  value={actor}
                  onChange={(e) => setActor(e.target.value.toUpperCase())}
                  placeholder="TELE001"
                />
              </div>
              <div>
                <Label>Subject type</Label>
                <select
                  value={aggregateType}
                  onChange={(e) =>
                    setAggregateType(e.target.value as PiiAggregateType | '')
                  }
                  className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-navy-900 hover:border-navy-200 focus-visible:outline-none focus-visible:border-navy-500"
                >
                  <option value="">Any subject</option>
                  <option value="Case">Case</option>
                  <option value="Customer">Customer</option>
                  <option value="AuditEvent">AuditEvent</option>
                  <option value="ErasureRequest">ErasureRequest</option>
                </select>
              </div>
              <div>
                <Label>Subject ID</Label>
                <Input
                  value={aggregateId}
                  onChange={(e) => setAggregateId(e.target.value)}
                  placeholder="UUID or text"
                />
              </div>
            </div>

            {/* Kind chips — VIEW / LIST / EXPORT */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-2xs uppercase tracking-wider text-navy-700">
                Kind
              </span>
              <Chip active={kind === ''} onClick={() => setKind('')}>
                All
              </Chip>
              <Chip active={kind === 'VIEW'} onClick={() => setKind('VIEW')}>
                View
              </Chip>
              <Chip active={kind === 'LIST'} onClick={() => setKind('LIST')}>
                List
              </Chip>
              <Chip active={kind === 'EXPORT'} onClick={() => setKind('EXPORT')}>
                Export
              </Chip>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Results */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Reads</CardTitle>
              <CardDescription>
                {total != null ? `${num(total)} match · newest first` : '—'}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={!reads || reads.length === 0}
              title="Downloads the reads on this page"
            >
              <Download className="h-3.5 w-3.5" />
              Download page (CSV)
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {isPending && <SkeletonRows rows={6} cols={6} />}
          {forbidden && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-navy-900">
                You don&rsquo;t have permission to view the PII access trail.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Ask your tenant admin to grant{' '}
                <span className="font-mono text-navy-700">
                  PII_ACCESS_AUDIT_VIEW
                </span>
                .
              </p>
            </div>
          )}
          {otherError && (
            <div className="px-5 pb-4 text-sm text-negative">
              Couldn&rsquo;t load the PII access trail. Try narrowing the window
              and reloading.
            </div>
          )}
          {reads && reads.length === 0 && (
            <div className="px-5 py-10 text-center">
              <p className="text-sm font-medium text-navy-900">
                No reads in this window.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Either nobody&rsquo;s looked at anything, or the filters are too
                narrow.
              </p>
            </div>
          )}
          {reads && reads.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
                  <tr>
                    <th className="px-5 py-2 font-medium">Time</th>
                    <th className="px-3 py-2 font-medium">Actor</th>
                    <th className="px-3 py-2 font-medium">Kind</th>
                    <th className="px-3 py-2 font-medium">Subject</th>
                    <th className="px-3 py-2 font-medium">Rows / Bytes</th>
                    <th className="px-5 py-2 font-medium">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {reads.map((r) => (
                    <ReadRow key={r.readId} record={r} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {reads && reads.length > 0 && (
            <AuditPager
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              rowsOnPage={reads.length}
              total={total ?? null}
              emptyLabel="No matching reads"
              loading={isPlaceholderData}
            />
          )}
        </CardContent>
        <CardFooter>
          <span className="text-2xs uppercase tracking-wider text-muted-foreground">
            18-month retention · per-tenant · DPDP §11
          </span>
        </CardFooter>
      </Card>
    </div>
  )
}

function ReadRow({ record }: { record: PiiAccessRecord }) {
  const tone = toneForKind(record.accessKind)
  const subject = record.aggregateId
    ? `${record.aggregateType} · ${record.aggregateId}`
    : record.route
  return (
    <tr className="border-b border-slate-200 last:border-0 transition-colors hover:bg-navy-50">
      <td className="px-5 py-2 align-top">
        <div className="text-navy-900">{relativeTime(record.accessedAt)}</div>
        <div className="text-2xs text-muted-foreground" title={dateTime(record.accessedAt)}>
          {dateTime(record.accessedAt)}
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="font-mono text-xs text-navy-900">
          {record.actorEmpId ?? (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
        {record.actorRoles.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {record.actorRoles.map((role) => (
              <span
                key={role}
                className="rounded border border-slate-200 bg-white px-1 py-px text-[10px] uppercase tracking-wider text-navy-700"
              >
                {role}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <Badge tone={tone}>{record.accessKind}</Badge>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="break-all text-xs text-navy-900">{subject}</div>
        {record.aggregateId && (
          <div className="text-2xs text-muted-foreground">{record.route}</div>
        )}
      </td>
      <td className="px-3 py-2 align-top text-xs tabular-nums text-navy-900">
        <div>{record.rowsReturned == null ? '—' : `${num(record.rowsReturned)} rows`}</div>
        <div className="text-2xs text-muted-foreground">
          {record.bytesReturned == null ? '—' : formatBytes(record.bytesReturned)}
        </div>
      </td>
      <td className="px-5 py-2 align-top font-mono text-2xs text-muted-foreground">
        <span className="block max-w-[12rem] truncate" title={record.clientIp ?? ''}>
          {record.clientIp ?? '—'}
        </span>
      </td>
    </tr>
  )
}

/** EXPORT stands out in amber; LIST in blue (informational, wide-net read);
 * VIEW is the default navy. */
function toneForKind(
  kind: PiiAccessKind,
): 'navy' | 'info' | 'warning' {
  switch (kind) {
    case 'EXPORT': return 'warning'
    case 'LIST':   return 'info'
    case 'VIEW':   return 'navy'
  }
}

/** The earlier of two ISO instants. */
function earlier(a: string, b: string): string {
  return new Date(a) <= new Date(b) ? a : b
}

function isoDate(d: Date): string {
  return localDate(d)
}

function csvCell(v: string): string {
  if (/[",\n]/.test(v)) {
    return '"' + v.replace(/"/g, '""') + '"'
  }
  return v
}

/** Compact byte sizes: "64 KB", "2.1 MB". Server is always whole bytes. */
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
}
